import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tasksApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import { TaskCard } from '../components/TaskCard';
import { clsx } from 'clsx';
import { TranslationKey } from '../i18n/translations';

const PLATFORMS: { id: string; labelKey: TranslationKey }[] = [
  { id: 'all', labelKey: 'all' },
  { id: 'instagram', labelKey: 'platform_instagram' },
  { id: 'reddit', labelKey: 'platform_reddit' },
  { id: 'google_maps', labelKey: 'platform_google_maps' },
  { id: 'twitter', labelKey: 'platform_twitter' },
  { id: 'app_store', labelKey: 'platform_app_store' },
  { id: 'youtube', labelKey: 'platform_youtube' },
];

export function TasksPage() {
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const { t } = useLanguage();

  const { data: tasks, isLoading } = useQuery({
    queryKey: ['tasks', selectedPlatform],
    queryFn: () =>
      tasksApi.list({
        limit: 50,
        platform: selectedPlatform === 'all' ? undefined : selectedPlatform,
      }),
  });

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <h1 className="text-xl font-bold text-tg">{t('available_tasks')}</h1>

      {/* Platform Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {PLATFORMS.map((platform) => (
          <button
            key={platform.id}
            onClick={() => setSelectedPlatform(platform.id)}
            className={clsx(
              'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              selectedPlatform === platform.id
                ? 'bg-brand-primary text-white'
                : 'bg-tg text-tg-hint'
            )}
          >
            {t(platform.labelKey)}
          </button>
        ))}
      </div>

      {/* Task List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-tg rounded-xl p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-12 h-12 bg-tg-secondary rounded-xl" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-tg-secondary rounded w-3/4" />
                  <div className="h-3 bg-tg-secondary rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (tasks as any[])?.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-tg-hint">{t('no_tasks_platform')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(tasks as any[])?.map((task: any) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
}
